/**
 * Ramadan door signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const ramadanDoorSignsPack: ToilePack = {
    slug: 'ramadan-door-signs',
    category: 'season',
    name: 'Ramadan door signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'welcome', label: 'Welcome sign', subject: 'a front-door sign lettered "Ramadan Mubarak, Welcome" under a crescent and a row of small lanterns, a hole at each top corner for a ribbon'},
        {key: 'hanger', label: 'Door hanger', subject: 'a tall door-hanger shape with a round cut-out at the top, lettered "Shh... Praying" with a prayer mat drawn below, dashed cut line around the whole shape'},
        {key: 'fasting', label: 'Fasting note', subject: 'an office desk or door sign lettered "I am fasting today" with a crescent and a small clock, and a blank line beneath labelled "Iftar at"'},
    ],
    styles: [
        {key: 'outline', label: 'Outline', style: 'bold black outlines on empty shapes, the drawings left to colour in', palette: 'black ink only', framing: 'The pieces spaced apart so each can be cut cleanly'},
        {key: 'geometric', label: 'Geometric', style: 'flat geometric print of eight-pointed stars and crescents with solid colour fills and crisp edges', palette: 'teal, gold and white', framing: 'The coloured pieces spaced apart on the white page'},
        {key: 'pastel', label: 'Pastel', style: 'soft flat illustration with rounded shapes and a gentle dotted border on every piece', palette: 'mint, blush, butter yellow and lavender', framing: 'The pieces spaced apart on the white page'},
    ],
    directions: ['classroom', 'Quran time', 'do not disturb'],
    directionHint: 'try: classroom',
    seo: {
        title: 'Ramadan door sign: welcome, door hanger, fasting note',
        metaDescription: 'Nine printable Ramadan door signs and hangers: a welcome sign, a quiet-please door hanger and a fasting note to cut out.',
        h1: 'Ramadan door signs: welcome sign, door hanger, fasting note',
        tagline: 'Nine printable signs, three designs each in outline, geometric and pastel finishes.',
        intro: 'Printable signs for a home, a classroom or an office during the month. A welcome sign for the front door is lettered Ramadan Mubarak, Welcome. A door hanger reads Shh... Praying above a prayer mat, for a bedroom or a study. A desk or door note reads I am fasting today, with a blank line for the iftar time. Cut along the dashed lines. Type a direction such as classroom or Quran time and the sign is drawn with it.',
        useCases: [
            'a welcome sign on the front door',
            'a quiet-please hanger for a bedroom or study',
            'a classroom sign during the month',
            'a desk note at the office',
        ],
        faq: [
            {q: 'What does the fasting note say?', a: 'I am fasting today, with a crescent and a small clock, and a blank line labelled Iftar at.'},
            {q: 'How is the door hanger cut?', a: 'It has a round cut-out at the top and a dashed cut line around the whole shape.'},
            {q: 'What are the three styles?', a: 'Outline is black ink to colour in, geometric is a flat vector print of stars and crescents, and pastel is soft flat illustration with a dotted border.'},
            {q: 'Can I ask for a different message?', a: 'Yes. Type a direction such as do not disturb and the sign is drawn with it. That is a request, not a promised result.'},
        ],
    },
}
